import * as React from 'react'
import { useTheme } from '../providers/ThemeProvider'

// ═══════════════════════════════════════════════════════════════════════════
// Badge - Washi Tape / Sticker Style for Diary Theme
// ═══════════════════════════════════════════════════════════════════════════

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success'
}

export function Badge({ className = '', variant = 'default', style, ...props }: BadgeProps) {
  const { isDiary } = useTheme()
  
  const variants = {
    default: 'border-transparent bg-surface-alt text-secondary',
    secondary: 'border-transparent bg-brand-subtle text-brand-hover',
    destructive: 'border-transparent bg-bad/20 text-bad',
    outline: 'text-secondary border-soft',
    success: 'border-transparent bg-ok/20 text-ok',
  }
  
  // Diary theme styles - Washi tape / sticker aesthetic
  const getDiaryStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      fontFamily: 'var(--font-ui)',
      border: 'none',
      borderRadius: '2px',
    }
    
    switch (variant) {
      case 'default':
        return {
          ...baseStyles,
          backgroundColor: 'var(--washi-tape-blue, #E1EBF8)',
          color: 'var(--ink-sepia)',
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)',
        }
      case 'secondary':
        return {
          ...baseStyles,
          backgroundColor: 'var(--washi-tape-pink, #F8E1E7)',
          color: 'var(--ink-sepia)',
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)',
        }
      case 'destructive':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(196, 92, 74, 0.2)',
          color: 'var(--bookmark-red)',
        }
      case 'success':
        return {
          ...baseStyles,
          backgroundColor: 'rgba(47, 133, 90, 0.15)',
          color: 'var(--semantic-success)',
        }
      case 'outline':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: 'var(--ink-faded)',
          border: '1px dashed var(--ink-faded)',
          borderRadius: '4px',
        }
      default:
        return baseStyles
    }
  }
  
  // 2026-08-29 UI audit: `ring-ring` is a shadcn-era class that emits zero css.
  return (
    <div
      className={`
        inline-flex items-center rounded-full border px-2.5 py-0.5 
        text-xs font-semibold transition-colors 
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface
        ${!isDiary ? variants[variant] : ''}
        ${className}
      `}
      style={isDiary ? { ...getDiaryStyles(), ...style } : style}
      {...props}
    />
  )
}

